import React, { useState, useEffect, useCallback, forwardRef, useRef } from 'react';
import { Input, Spin, Card } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, EnvironmentOutlined, HomeOutlined } from '@ant-design/icons';
import geocodingService from '../services/geocodingService';
import { getSitesByOrganization } from '../services/siteService';
import { useAuth } from '../contexts/AuthContext';

/**
 * LocationInput - Enhanced location input with Google Places Autocomplete + Internal Sites + Geocoding Validation
 * 
 * PHASE 3 HYBRID APPROACH (FIXED: Non-blocking input):
 * - Immediate, responsive typing experience (never blocked)
 * - Background Google Places Autocomplete for external addresses
 * - Background Internal Sites Database for organization-specific locations
 * - Background geocoding validation (completely separate from typing)
 * - Click-to-select from both external and internal suggestions
 * - Graceful fallback to validation-only mode
 * - Zero risk to existing functionality
 */
const LocationInput = forwardRef(({ 
  value = '', 
  onChange, 
  placeholder = 'Start typing an address or site name...', 
  disabled = false,
  className = '',
  style = {},
  required = false,
  ...props 
}, ref) => {
  const { currentUser } = useAuth();
  
  // 🎯 FULLY CONTROLLED COMPONENT - NO INTERNAL INPUT STATE
  const [validationState, setValidationState] = useState('idle'); // Background validation
  const [validationMessage, setValidationMessage] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [placesService, setPlacesService] = useState(null);
  const [internalSites, setInternalSites] = useState([]);
  
  // Timers for background operations (never block input)
  const suggestionsTimerRef = useRef(null);
  const validationTimerRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Initialize Google Places service (background)
  useEffect(() => {
    const initializePlaces = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        const service = new window.google.maps.places.AutocompleteService();
        setPlacesService(service);
        console.log('✅ Google Places Autocomplete service initialized');
      } else {
        console.warn('⚠️ Google Maps API not available - falling back to validation-only mode');
      }
    };

    if (window.google) {
      initializePlaces();
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google) {
          initializePlaces();
          clearInterval(checkGoogle);
        }
      }, 100);
      setTimeout(() => clearInterval(checkGoogle), 5000);
    }
  }, []);

  // 🏢 PHASE 3: Load internal sites for the organization (background)
  useEffect(() => {
    const loadInternalSites = async () => {
      if (!currentUser?.orgId) {
        console.log('🏢 No organization ID available for internal sites');
        setInternalSites([]);
        return;
      }

      try {
        console.log('🏢 Loading internal sites for organization:', currentUser.orgId);
        const sites = await getSitesByOrganization(currentUser.orgId);
        
        const usableSites = sites.filter(site => 
          site.site_name && 
          site.site_name.trim().length > 0 &&
          site.latitude && 
          site.longitude
        );
        
        setInternalSites(usableSites);
        console.log(`🏢 Loaded ${usableSites.length} usable internal sites (${sites.length} total)`);
      } catch (error) {
        console.warn('⚠️ Failed to load internal sites:', error);
        setInternalSites([]);
      }
    };

    loadInternalSites();
  }, [currentUser?.orgId]);

  // 🏢 PHASE 3: Search internal sites (fast, synchronous)
  const getInternalSiteSuggestions = useCallback((input) => {
    if (!input || input.trim().length < 10 || internalSites.length === 0) {
      return [];
    }

    const searchTerm = input.trim().toLowerCase();
    
    const matchingSites = internalSites
      .filter(site => {
        const searchableText = [
          site.site_name,
          site.address_line_1,
          site.address_line_2,
          site.suburb,
          site.region,
          site.site_type
        ].join(' ').toLowerCase();
        
        return searchableText.includes(searchTerm);
      })
      .slice(0, 3)
      .map((site, index) => ({
        id: `internal_${site.id}`,
        type: 'internal_site',
        siteId: site.id,
        description: site.site_name,
        mainText: site.site_name,
        secondaryText: `${site.site_type || 'Site'} • ${site.suburb || site.region || 'Internal Location'}`,
        fullAddress: [site.address_line_1, site.address_line_2, site.suburb, site.state, site.postcode].filter(Boolean).join(', '),
        coordinates: {
          latitude: site.latitude,
          longitude: site.longitude
        },
        siteData: site,
        index: -1000 + index
      }));

    console.log(`🏢 Found ${matchingSites.length} internal site matches for "${searchTerm}"`);
    return matchingSites;
  }, [internalSites]);

  // 📍 Get Google Places suggestions (async, background)
  const getGooglePlacesSuggestions = useCallback(async (input) => {
    if (!placesService || !input || input.trim().length < 10) {
      return [];
    }

    return new Promise((resolve) => {
      const request = {
        input: input.trim(),
        componentRestrictions: { country: 'au' },
        types: ['address', 'establishment', 'geocode']
      };

      placesService.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const placesSuggestions = predictions.slice(0, 4).map((prediction, index) => ({
            id: prediction.place_id,
            type: 'google_places',
            description: prediction.description,
            mainText: prediction.structured_formatting?.main_text || prediction.description,
            secondaryText: prediction.structured_formatting?.secondary_text || '',
            index: index + 100
          }));
          resolve(placesSuggestions);
        } else {
          resolve([]);
        }
      });
    });
  }, [placesService]);

  // 🎯 BACKGROUND SUGGESTIONS UPDATER (never blocks input)
  const updateSuggestions = useCallback(async (searchValue) => {
    try {
      setIsLoadingSuggestions(true);
      
      // Get internal sites (fast, synchronous)
      const internalSuggestions = getInternalSiteSuggestions(searchValue);
      
      // Get Google Places (async, background)
      const placesSuggestions = await getGooglePlacesSuggestions(searchValue);
      
      // Combine suggestions
      const allSuggestions = [...internalSuggestions, ...placesSuggestions];
      
      setSuggestions(allSuggestions);
      setShowSuggestions(allSuggestions.length > 0);
      setIsLoadingSuggestions(false);
      
      console.log(`📍 Updated suggestions: ${internalSuggestions.length} internal + ${placesSuggestions.length} external = ${allSuggestions.length} total`);
      
    } catch (error) {
      console.warn('⚠️ Error updating suggestions:', error);
      setIsLoadingSuggestions(false);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [getInternalSiteSuggestions, getGooglePlacesSuggestions]);

  // 🔍 BACKGROUND VALIDATION (never blocks input)
  const validateLocation = useCallback(async (locationText) => {
    if (!locationText || locationText.trim().length < 12) {
      setValidationState('idle');
      setValidationMessage('');
      return;
    }

    const cleanLocation = locationText.trim();
    console.log('🔍 BACKGROUND VALIDATION: Geocoding location:', cleanLocation);

    try {
      setValidationState('validating');
      setValidationMessage('Verifying address...');

      const result = await geocodingService.geocodeCrisisLocation(cleanLocation);

      if (result.success) {
        setValidationState('valid');
        setValidationMessage(`✓ Verified: ${result.formattedAddress || cleanLocation}`);
        console.log('✅ BACKGROUND VALIDATION: Geocoding successful:', result);
      } else {
        setValidationState('invalid');
        setValidationMessage('⚠️ Address not recognized - you can still proceed');
        console.log('⚠️ BACKGROUND VALIDATION: Geocoding failed:', result.error);
      }
    } catch (error) {
      console.warn('⚠️ BACKGROUND VALIDATION: Geocoding error:', error);
      setValidationState('invalid');
      setValidationMessage('⚠️ Unable to verify address - you can still proceed');
    }
  }, []);

  // 🎯 IMMEDIATE, NON-BLOCKING INPUT HANDLER - FULLY CONTROLLED
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // 1. NOTIFY PARENT IMMEDIATELY - LET PARENT CONTROL THE VALUE
    if (onChange) {
      onChange(e);
    }

    // 2. CLEAR OLD BACKGROUND TIMERS
    if (suggestionsTimerRef.current) {
      clearTimeout(suggestionsTimerRef.current);
    }
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }

    // 3. RESET VALIDATION STATE (visual feedback only)
    if (validationState !== 'idle') {
      setValidationState('idle');
      setValidationMessage('');
    }

    // 4. SCHEDULE BACKGROUND OPERATIONS (never block input)
    if (newValue && newValue.trim().length >= 10) {
      // Fast suggestions (500ms) - only after substantial typing
      suggestionsTimerRef.current = setTimeout(() => {
        updateSuggestions(newValue);
      }, 500);

      // Slow validation (1500ms) - only for longer addresses
      if (newValue.trim().length >= 12) {
        validationTimerRef.current = setTimeout(() => {
          validateLocation(newValue);
        }, 1500);
      }
    } else {
      // Clear suggestions for short inputs
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
    }
  }, [onChange, updateSuggestions, validateLocation, validationState]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(async (suggestion) => {
    console.log('📍 SELECTION: User selected suggestion:', {
      type: suggestion.type,
      description: suggestion.description,
      hasCoordinates: !!suggestion.coordinates
    });
    
    // 1. Update parent with selected value
    const displayValue = suggestion.type === 'internal_site' ? 
      suggestion.fullAddress || suggestion.description : 
      suggestion.description;
    
    const syntheticEvent = {
      target: { value: displayValue }
    };
    
    if (onChange) {
      onChange(syntheticEvent);
    }

    // 2. Hide suggestions
    setShowSuggestions(false);
    setSuggestions([]);

    // 3. Handle internal site selection (already has coordinates)
    if (suggestion.type === 'internal_site' && suggestion.coordinates) {
      setValidationState('valid');
      setValidationMessage(`✓ Internal site: ${suggestion.description}`);
      console.log('✅ SELECTION: Internal site selected (coordinates available):', suggestion.coordinates);
      return;
    }

    // 4. Handle external address selection (needs geocoding)
    setValidationState('validating');
    setValidationMessage('Processing selected address...');
    
    try {
      const result = await geocodingService.geocodeCrisisLocation(suggestion.description);
      
      if (result.success) {
        setValidationState('valid');
        setValidationMessage(`✓ Address confirmed: ${result.formattedAddress || suggestion.description}`);
        console.log('✅ SELECTION: Geocoding successful for selected address:', result);
      } else {
        setValidationState('invalid');
        setValidationMessage('⚠️ Selected address could not be verified');
        console.log('⚠️ SELECTION: Geocoding failed for selected address:', result.error);
      }
    } catch (error) {
      console.warn('⚠️ SELECTION: Geocoding error for selected address:', error);
      setValidationState('invalid');
      setValidationMessage('⚠️ Error processing selected address');
    }
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSuggestions([]);
        break;
      default:
        break;
    }
  }, [showSuggestions, suggestions]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      console.log('🎯 CLICK OUTSIDE CHECK:', {
        hasInput: !!inputRef.current,
        hasSuggestions: !!suggestionsRef.current,
        clickedElement: event.target,
        showSuggestions
      });

      // Don't hide if not showing suggestions
      if (!showSuggestions) return;

      // Don't hide if clicking inside input
      if (inputRef.current && inputRef.current.contains(event.target)) {
        console.log('🎯 Click inside input - keeping suggestions');
        return;
      }

      // Don't hide if clicking inside suggestions
      if (suggestionsRef.current && suggestionsRef.current.contains(event.target)) {
        console.log('🎯 Click inside suggestions - keeping suggestions');
        return;
      }

      // Don't hide if clicking on a suggestion item (additional safety check)
      const suggestionItem = event.target.closest('[data-suggestion-item]');
      if (suggestionItem) {
        console.log('🎯 Click on suggestion item - keeping suggestions');
        return;
      }

      console.log('🎯 Click outside - hiding suggestions');
      setShowSuggestions(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
      if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
    };
  }, []);

  // Get validation icon (background indicator only)
  const getValidationIcon = () => {
    if (isLoadingSuggestions) {
      return <Spin size="small" />;
    }
    
    switch (validationState) {
      case 'validating':
        return <Spin size="small" />;
      case 'valid':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'invalid':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return null;
    }
  };

  // Get input status for styling (non-blocking)
  const getInputStatus = () => {
    switch (validationState) {
      case 'valid':
        return 'success';
      case 'invalid':
        return 'warning';
      default:
        return '';
    }
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <Input
        ref={(node) => {
          inputRef.current = node;
          if (ref) {
            if (typeof ref === 'function') {
              ref(node);
            } else {
              ref.current = node;
            }
          }
        }}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        style={style}
        status={getInputStatus()}
        suffix={getValidationIcon()}
        {...props}
      />
      
      {/* 🎯 NON-BLOCKING SUGGESTIONS DROPDOWN */}
      {showSuggestions && suggestions.length > 0 && (
        <Card
          ref={suggestionsRef}
          size="small"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1050,
            marginTop: '4px',
            maxHeight: '250px',
            overflowY: 'auto',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}
          bodyStyle={{ padding: 0 }}
          onMouseDown={(e) => {
            // Prevent input blur when interacting with suggestions
            e.preventDefault();
          }}
          onMouseEnter={() => {
            console.log('🎯 Mouse entered suggestions dropdown');
          }}
        >
          {suggestions.map((suggestion, index) => {
            const isInternal = suggestion.type === 'internal_site';
            const icon = isInternal ? (
              <HomeOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
            ) : (
              <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
            );
            
            return (
              <div
                key={suggestion.id}
                data-suggestion-item="true"
                onMouseDown={(e) => {
                  // Prevent input from losing focus when clicking suggestion
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🎯 SUGGESTION CLICKED:', suggestion.description);
                  handleSuggestionSelect(suggestion);
                }}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: isInternal ? '#f6ffed' : 'transparent',
                  userSelect: 'none' // Prevent text selection on click
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isInternal ? '#d9f7be' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isInternal ? '#f6ffed' : 'transparent';
                }}
              >
                {icon}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#262626', 
                    fontWeight: isInternal ? 600 : 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {suggestion.mainText}
                    {isInternal && (
                      <span style={{ 
                        fontSize: '10px', 
                        background: '#52c41a', 
                        color: 'white', 
                        padding: '1px 4px', 
                        borderRadius: '2px',
                        fontWeight: 500
                      }}>
                        SITE
                      </span>
                    )}
                  </div>
                  {suggestion.secondaryText && (
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      {suggestion.secondaryText}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      )}
      
      {/* Background validation message (non-blocking) */}
      {validationState !== 'idle' && validationMessage && (
        <div 
          style={{ 
            marginTop: '4px', 
            fontSize: '12px',
            color: validationState === 'valid' ? '#52c41a' : 
                   validationState === 'invalid' ? '#faad14' : '#666',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {validationMessage}
        </div>
      )}
    </div>
  );
});

LocationInput.displayName = 'LocationInput';

export default LocationInput; 